import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { colors } from '@guryou/colors';
import {
  createReservationAsAnonymous,
  getPersonnelAvailabilityDates,
  getPersonnelAvailabilitySlots,
  useEffectOnSuccess,
  useEffectOnError,
  NotificationType,
  NOTIFICATION,
} from '@guryou/redux';
import { printInputDate, printPrice, printTime } from '@guryou/utils';
import { Modal, Icon } from '../../components/shared';
import { WidgetContext } from './WidgetContext';

const ServiceBookingModal = ({ subservice, dispatch, localData, setLocalData, personnelTimeSlots, personnelDateSlots, closeModal }) => {
  const { t } = useTranslation();
  const [showHours, setShowHours] = useState(false);
  const { askFirstname, askLastname, askPhone, user, primaryColor, setActiveServiceId } = useContext(WidgetContext);

  // Use custom primary color if available, otherwise fallback to default mandy color
  const primary = primaryColor || 'var(--feel-primary)';
  const secondary = primaryColor || colors.cabaret;

  // Local loading states for better UX
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      surname: user?.lastname || '',
      phone: user?.phone || '',
      resourceId: subservice.resources.length === 1 ? subservice.resources[0]?.id : null,
      date: localData.date,
      time: localData.time,
    },
  });

  const watchedResourceId = watch('resourceId');
  const watchedDate = watch('date');

  const onSubmit = (data, onlyBook) => {
    setIsSubmitting(true);
    dispatch(
      createReservationAsAnonymous({
        subServiceId: subservice.id,
        dateTimeFrom: `${printInputDate(data.date)}T${data.time}`,
        email: data.email,
        firstname: data.name,
        phone: data.phone,
        resourceId: Number(data.resourceId),
        lastname: data.surname,
        onlyBook,
      })
    );
  };

  // Handle success
  useEffectOnSuccess([state => state.widget.reservationCreate], reserve => {
    setIsSubmitting(false);
    setActiveServiceId(null);

    if (!reserve?.id) {
      // Success toast for email verification
      dispatch({
        type: NOTIFICATION,
        payload: {
          message: t('GENERAL.checkEmailToConfirm'),
          variant: NotificationType.Success,
        },
      });
    } else {
      // Success toast for reservation created
      dispatch({
        type: NOTIFICATION,
        payload: {
          message: t('GENERAL.reservationCreated'),
          variant: NotificationType.Success,
        },
      });
    }
    closeModal();
  });

  // Handle errors
  useEffectOnError([state => state.widget.reservationCreate], errors => {
    setIsSubmitting(false);
    // Error toast
    dispatch({
      type: NOTIFICATION,
      payload: {
        message: errors?.[0]?.message || t('GENERAL.somethingWrong'),
        variant: NotificationType.Error,
      },
    });
  });

  // Initialize form with single resource if available
  useEffect(() => {
    if (subservice.resources.length === 1) {
      const resourceId = Number(subservice.resources[0]?.id);
      setValue('resourceId', resourceId);
      setLocalData(prevData => ({ ...prevData, resourceId }));
      setIsLoadingDates(true);
      dispatch(getPersonnelAvailabilityDates(subservice.id, resourceId));
    }
  }, [subservice.resources, subservice.id, setValue, setLocalData, dispatch]);

  // Watch for resource changes - optimized to prevent unnecessary re-renders
  useEffect(() => {
    if (watchedResourceId && watchedResourceId !== localData.resourceId) {
      const resourceId = Number(watchedResourceId);
      setLocalData(prevData => ({ ...prevData, resourceId }));
      setIsLoadingDates(true);
      dispatch(getPersonnelAvailabilityDates(subservice.id, resourceId));
      // Reset date and time when resource changes
      setValue('date', '');
      setValue('time', '');
      setShowHours(false);
    }
  }, [watchedResourceId, localData.resourceId, dispatch, subservice.id, setLocalData, setValue]);

  // Watch for date changes - optimized to prevent unnecessary re-renders
  useEffect(() => {
    if (watchedDate && watchedResourceId && watchedDate !== localData.date) {
      setLocalData(prevData => ({ ...prevData, date: watchedDate }));
      setIsLoadingSlots(true);
      dispatch(
        getPersonnelAvailabilitySlots(subservice.id, Number(watchedResourceId), printInputDate(watchedDate), undefined, undefined, false, null, true)
      );
      setShowHours(true);
      // Reset time when date changes
      setValue('time', '');
    }
  }, [watchedDate, watchedResourceId, localData.date, dispatch, subservice.id, setLocalData, setValue]);

  // Watch for API responses to update loading states using useEffectOnSuccess
  useEffectOnSuccess([state => state.widget.personnelDateSlots], () => {
    setIsLoadingDates(false);
  });

  useEffectOnSuccess([state => state.widget.personnelTimeSlots], () => {
    setIsLoadingSlots(false);
  });

  return (
    <Modal isOpen={true} onClose={closeModal} title={subservice?.name} maxWidth="4xl">
      <div className="p-6">
        {/* Price Display */}
        <div
          className="relative overflow-hidden rounded-2xl p-6 mb-8 shadow-lg"
          style={{ background: `linear-gradient(to bottom right, ${primary}, ${secondary})` }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Icon name="euro" size="md" color="white" />
                </div>
                <div>
                  <p className="text-white/90 text-sm font-medium">{t('GENERAL.price')}</p>
                  <p className="text-white/70 text-xs">{subservice?.name}</p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-3xl font-bold text-white">{printPrice(subservice.price)}</span>
                <p className="text-white/80 text-xs mt-1">per session</p>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Steps */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Step 1: Resource Selection */}
          <div className="relative">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-4 text-white" style={{ backgroundColor: primary }}>
                <Icon name="user" size="sm" className="text-current" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('PROFILE.resource')}</h3>
                <p className="text-sm text-gray-600">{t('SERVICES.staffStepDescription')}</p>
              </div>
            </div>

            <div className="ml-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="resourceId" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('SERVICES.staff')} *
                  </label>
                  <select
                    {...register('resourceId', { required: t('GENERAL.resourceRequired') })}
                    id="resourceId"
                    style={{
                      borderColor: errors.resourceId ? '#ef4444' : '#d1d5db',
                    }}
                    className="w-full h-12 px-4 py-3 border rounded-lg focus:ring-2 transition-colors duration-200"
                    onFocus={e => {
                      e.currentTarget.style.borderColor = primary;
                      e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                    }}
                    onBlur={e => {
                      if (!errors.resourceId) {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <option value="">{t('PROFILE.resource')}</option>
                    {subservice.resources?.map(resource => (
                      <option key={resource.id} value={resource.id}>
                        {resource.name}
                      </option>
                    ))}
                  </select>
                  {errors.resourceId && <p className="mt-1 text-sm text-red-600">{errors.resourceId.message}</p>}
                </div>

                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('GENERAL.pleaseSelectDate')} *
                    {isLoadingDates && (
                      <span className="ml-2 text-xs text-mandy">
                        <Icon name="refresh" size="xs" className="animate-spin inline mr-1" />
                        Loading dates...
                      </span>
                    )}
                  </label>
                  <input
                    {...register('date', { required: t('GENERAL.dateRequired') })}
                    type="date"
                    id="date"
                    min={new Date().toISOString().split('T')[0]}
                    disabled={!watchedResourceId || isLoadingDates}
                    style={{
                      borderColor: errors.date ? '#ef4444' : '#d1d5db',
                    }}
                    className={`w-full h-12 px-4 py-3 border rounded-lg focus:ring-2 transition-colors duration-200 ${
                      !watchedResourceId || isLoadingDates ? 'bg-gray-100' : 'bg-white'
                    }`}
                    onFocus={e => {
                      e.currentTarget.style.borderColor = primary;
                      e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                    }}
                    onBlur={e => {
                      if (!errors.date) {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  />
                  {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>}
                </div>
              </div>

              {showHours && watchedResourceId && (
                <div className="mt-4">
                  <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('RESERVATIONS.reservationTime')} *
                    {isLoadingSlots && (
                      <span className="ml-2 text-xs text-mandy">
                        <Icon name="refresh" size="xs" className="animate-spin inline mr-1" />
                        Loading times...
                      </span>
                    )}
                  </label>
                  <select
                    {...register('time', { required: t('GENERAL.timeRequired') })}
                    id="time"
                    disabled={!personnelTimeSlots.data?.length || isLoadingSlots}
                    style={{
                      borderColor: errors.time ? '#ef4444' : '#d1d5db',
                    }}
                    className={`w-full h-12 px-4 py-3 border rounded-lg focus:ring-2 transition-colors duration-200 ${
                      !personnelTimeSlots.data?.length || isLoadingSlots ? 'bg-gray-100' : 'bg-white'
                    }`}
                    onFocus={e => {
                      e.currentTarget.style.borderColor = primary;
                      e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                    }}
                    onBlur={e => {
                      if (!errors.time) {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <option value="">{t('RESERVATIONS.reservationTime')}</option>
                    {personnelTimeSlots.data?.map(timeSlot => (
                      <option key={timeSlot} value={timeSlot}>
                        {printTime(timeSlot)}
                      </option>
                    ))}
                  </select>
                  {errors.time && <p className="mt-1 text-sm text-red-600">{errors.time.message}</p>}
                </div>
              )}
            </div>
          </div>

          {/* Step 2: Personal Data */}
          <div className="relative">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-4 text-white" style={{ backgroundColor: primary }}>
                <Icon name="info" size="sm" className="text-current" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('SERVICES.personalData')}</h3>
                <p className="text-sm text-gray-600">{t('SERVICES.personalDataStepDescription')}</p>
              </div>
            </div>

            <div className="ml-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    {askFirstname ? `${t('GENERAL.name')} *` : t('GENERAL.name')}
                  </label>
                  <input
                    {...register('name', {
                      required: askFirstname ? t('GENERAL.nameRequired') : false,
                    })}
                    type="text"
                    id="name"
                    disabled={!watchedResourceId || !watchedDate || !watch('time')}
                    style={{
                      borderColor: errors.name ? '#ef4444' : '#d1d5db',
                    }}
                    className={`w-full h-12 px-4 py-3 border rounded-lg focus:ring-2 transition-colors duration-200 ${
                      !watchedResourceId || !watchedDate || !watch('time') ? 'bg-gray-100' : 'bg-white'
                    }`}
                    onFocus={e => {
                      e.currentTarget.style.borderColor = primary;
                      e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                    }}
                    onBlur={e => {
                      if (!errors.name) {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                    placeholder={t('GENERAL.namePlaceholder')}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                </div>

                <div>
                  <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-2">
                    {askLastname ? `${t('GENERAL.surname')} *` : t('GENERAL.surname')}
                  </label>
                  <input
                    {...register('surname', {
                      required: askLastname ? t('GENERAL.surnameRequired') : false,
                    })}
                    type="text"
                    id="surname"
                    disabled={!watchedResourceId || !watchedDate || !watch('time')}
                    style={{
                      borderColor: errors.surname ? '#ef4444' : '#d1d5db',
                    }}
                    className={`w-full h-12 px-4 py-3 border rounded-lg focus:ring-2 transition-colors duration-200 ${
                      !watchedResourceId || !watchedDate || !watch('time') ? 'bg-gray-100' : 'bg-white'
                    }`}
                    onFocus={e => {
                      e.currentTarget.style.borderColor = primary;
                      e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                    }}
                    onBlur={e => {
                      if (!errors.surname) {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                    placeholder={t('GENERAL.surnamePlaceholder')}
                  />
                  {errors.surname && <p className="mt-1 text-sm text-red-600">{errors.surname.message}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('GENERAL.email')} *
                  </label>
                  <input
                    {...register('email', {
                      required: t('GENERAL.emailRequired'),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('GENERAL.emailInvalid'),
                      },
                    })}
                    type="email"
                    id="email"
                    disabled={!watchedResourceId || !watchedDate || !watch('time')}
                    style={{
                      borderColor: errors.email ? '#ef4444' : '#d1d5db',
                    }}
                    className={`w-full h-12 px-4 py-3 border rounded-lg focus:ring-2 transition-colors duration-200 ${
                      !watchedResourceId || !watchedDate || !watch('time') ? 'bg-gray-100' : 'bg-white'
                    }`}
                    onFocus={e => {
                      e.currentTarget.style.borderColor = primary;
                      e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                    }}
                    onBlur={e => {
                      if (!errors.email) {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                    placeholder={t('GENERAL.emailPlaceholder')}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    {askPhone ? `${t('GENERAL.phone')} *` : t('GENERAL.phone')}
                  </label>
                  <input
                    {...register('phone', {
                      required: askPhone ? t('GENERAL.phoneRequired') : false,
                    })}
                    type="tel"
                    id="phone"
                    disabled={!watchedResourceId || !watchedDate || !watch('time')}
                    style={{
                      borderColor: errors.phone ? '#ef4444' : '#d1d5db',
                    }}
                    className={`w-full h-12 px-4 py-3 border rounded-lg focus:ring-2 transition-colors duration-200 ${
                      !watchedResourceId || !watchedDate || !watch('time') ? 'bg-gray-100' : 'bg-white'
                    }`}
                    onFocus={e => {
                      e.currentTarget.style.borderColor = primary;
                      e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                    }}
                    onBlur={e => {
                      if (!errors.phone) {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                    placeholder={t('GENERAL.phonePlaceholder')}
                  />
                  {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleSubmit(data => onSubmit(data, true))}
              disabled={isSubmitting}
              style={{
                backgroundColor: 'white',
                borderColor: primary,
                color: primary,
              }}
              className={`flex-1 border-2 font-semibold px-6 py-3 rounded-xl transition-all duration-200 flex items-center justify-center ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onMouseEnter={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = primary;
                  e.currentTarget.style.color = 'white';
                }
              }}
              onMouseLeave={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.color = primary;
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-t-transparent mr-2" style={{ borderColor: primary }}></div>
                  {t('GENERAL.loading')}
                </>
              ) : (
                <>
                  <Icon name="book" size="sm" className="mr-2" />
                  {t('GENERAL.bookOnly')} {printPrice(subservice.price)}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleSubmit(data => onSubmit(data, false))}
              disabled={isSubmitting}
              style={{
                background: `linear-gradient(to right, ${primary}, ${secondary})`,
              }}
              className={`flex-1 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  {t('GENERAL.loading')}
                </>
              ) : (
                <>
                  <Icon name="shopping-cart" size="sm" className="mr-2" />
                  {t('GENERAL.payNow')} {printPrice(subservice.price)}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ServiceBookingModal;
